import { Plan, Subscription } from '@sxm-de/dev-kits/microservices-sdk/types';
import {
    DomainInitPayload,
    NextBestActionsByLocale,
    OfferDisplayInfo,
    SmsAccountPlan,
    SxmTypeAccount,
    SxmTypeDevice,
    SxmTypePlan,
    SxmTypeSubscription,
} from '@sxm-de/dev-kits/web-sdk/types';
import { getPackageCmsDisplayName } from '@sxm-de/dev-kits/web-sdk/domains/offers';
import {
    getInfoByCapabilities,
    mapSmsAccountToAccount,
    mapSmsDeviceToDevice,
    mapSmsPlanToPlan,
    mapSmsSubscriptionToSubscription,
} from '@sxm-de/dev-kits/web-sdk/utils';

export type GetSmsAccountDataFn = () => Promise<{
    nextBestAction: NextBestActionsByLocale;
    plans: SxmTypePlan[];
    offerDisplayInfo: OfferDisplayInfo;
    subscriptions: SxmTypeSubscription[];
    devices: SxmTypeDevice[];
    account: SxmTypeAccount;
}>;

export function initGetSmsAccountDataFn({
    dependencies,
}: DomainInitPayload): GetSmsAccountDataFn {
    const { microservicesSdk } = dependencies;
    return async () => {
        const nextBestActionPromise =
            microservicesSdk.domains.account.nextBestAction();

        const accountResponse =
            await microservicesSdk.domains.account.getAccount({
                includeFuturePaymentInfo: true,
                refreshAccount: true,
            });

        const hasInactiveServiceDueToNonPay =
            accountResponse.subscriptions?.length > 0 &&
            accountResponse.subscriptions.every(
                (sub) => sub.hasInactiveServiceDueToNonPay,
            );

        const currentQuote = hasInactiveServiceDueToNonPay
            ? /**
               * TODO:
               * determine how to make the quote data call
               */
              (
                  await microservicesSdk.domains.quotes.quote({
                      // radioId?: string;
                      // subscriptionId?: string;
                      // planCodes?: String[];
                      // followOnPlanCodes?: String[];
                      // renewalPlanCode?: string;
                      // serviceAddress?: Address;
                      // isCancelTrialFollowOnPlan?: boolean;
                  })
              ).quote.currentQuote
            : null;

        const packageIds = (accountResponse.subscriptions ?? [])
            .map((subscription) => {
                return [
                    ...new Set(subscription.plans.map((p) => p.packageName)),
                ];
            })
            .flat()
            .map(normalizeSmsPackageId);

        const offerDisplayInfo = await getPackageCmsDisplayName(
            { smsPackageIds: packageIds },
            dependencies,
        );

        const account = mapSmsAccountToAccount(accountResponse, currentQuote);

        const subscriptions = (accountResponse.subscriptions ?? []).map(
            (subscription) => mapSmsSubscriptionToSubscription(subscription),
        );

        const plans = (accountResponse.subscriptions ?? [])
            .map((subscription) => mapSmsPlanToPlan(subscription))
            .flat();

        const devices = [
            ...accountResponse.closedDevices.map((device) =>
                mapSmsDeviceToDevice(device),
            ),
            ...accountResponse.subscriptions
                .filter((subscription) => subscription.radioService)
                .map(
                    (subscription) =>
                        ({
                            ...mapSmsDeviceToDevice(subscription.radioService),
                            nickname: subscription.nickname ?? '',
                            /**
                             * TODO:
                             * Remove this once we have the device type coming from the api call
                             */
                            type: getInfoByCapabilities(
                                subscription.radioService.capabilities,
                            ).type,
                        }) as SxmTypeDevice,
                ),
        ].filter((device) => device !== null);

        return {
            // Wait for call made at start of method
            nextBestAction: await nextBestActionPromise,

            ///////////// properly mapped content:
            account,
            plans,
            offerDisplayInfo,
            subscriptions,
            devices,
        };
    };
}

/**
 * TODO:
 * Refactor this to live somewhere common
 */
export function getStartEndDatesForPastYear(): {
    startDateTime: string;
    endDateTime: string;
} {
    const now = new Date();
    const endDateTime = now.toISOString();
    const startDateTime = new Date(
        now.setDate(now.getDate() - 365),
    ).toISOString();
    return {
        startDateTime,
        endDateTime,
    };
}

/**
 * TODO:
 * Refactor this to live somewhere common
 */
function normalizeSmsPackageId(packageId: string): string {
    if (packageId.startsWith('3_')) {
        return packageId.replace(/^3_/, 'SXM_');
    }
    return packageId;
}
